import React, { useState } from 'react';
import {
    InfoCard,
    StructuredMetadataTable,
} from '@backstage/core-components';
import { toTitleCase } from './CustomDropdownFilter';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import {
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Collapse,
    IconButton,
    Tooltip,
    Box
} from '@material-ui/core';
import { ExpandMore as ExpandMoreIcon } from '@material-ui/icons';

type CapabilityEntry = {
    value: any;
    description: string;
};

// Helper: Filter out empty values from an object.
const filterProps = (obj: Record<string, any>) =>
    Object.fromEntries(
        Object.entries(obj).filter(([_, value]) => value != null && value !== '')
    );
type ApiEntitySpec = {
    contractTesting?: {
        isEnabled?: string | boolean;
        listOfApplications?: string[] | string;
        reason?: string;
    };
};

const PRESERVE_CASE_FIELDS = new Set([
    'LeanIX ID/ Troux ID',
    'Apigee Proxy',
    'Target Basepath',
    'Target Base Path',
    'URL Proxy',
    'URL proxy',
    'Open API Specification URL',
    'OpenAPI Specification URL',
    'Service Relation Graph',
    'Repository Name',
    'ServiceNow Assignment Group',
]);

const formatMetadataValue = (value: unknown, fieldName?: string) => {
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'string') {
        if (fieldName && PRESERVE_CASE_FIELDS.has(fieldName)) return value;
        const lowerValue = value.toLowerCase().trim();
        if (lowerValue === 'yes' || lowerValue === 'true'|| lowerValue=='Yes') return 'true';
        if (lowerValue === 'no' || lowerValue === 'false'|| lowerValue=='No') return 'false';
        return toTitleCase(value);
    }
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return value == null ? '' : String(value);
};

const formatReasonWithLineBreaks = (reason: string) =>
    reason.replace(/([.!?])\s+(?=[A-Z])/g, '$1\n').charAt(0).toUpperCase() + reason.slice(1);

const formatCapabilityValue = (key: string, value: unknown) => {
    const textValue = value == null ? '' : String(value).trim();
    if (!textValue) return 'N/A';

    if (key === 'Contract Testing: List Of Applications') {
        return textValue
            .split(',')
            .map(application => toTitleCase(application.trim()))
            .filter(Boolean)
            .join(', ')
    }
    return textValue;
};

// External Consumers Table with same header background as API Details.
const ExternalConsumersTable = ({ externalConsumers }: { externalConsumers: any[] }) => {
    const externaConsumerslength = externalConsumers?.length ?? 0;
    const [isExpanded, setIsExpanded] = useState(false);
    if (!externalConsumers || externaConsumerslength === 0) return null;
    return (
        <InfoCard>
            <Box display="flex" alignItems="left" justifyContent="flex-start">
                <IconButton
                        size="small"
                        onClick={() => setIsExpanded(!isExpanded)}
                        aria-label="Toggle External Consumers details"
                        aria-expanded={isExpanded}
                        style={{
                            transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                            transition: 'transform 0.3s',
                            padding: 0,
                            backgroundColor: 'transparent',
                        }}
                        disableRipple
                    >
                        <ExpandMoreIcon />
                </IconButton>
                <Box style={{ flex: 1 }}>
                    <div style={{padding: '10px',fontSize:'20px',fontWeight:'bold' }}>External Consumers ({externaConsumerslength})</div>

                </Box>
            </Box>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <div style={{ width: '100%' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Onboarding Date</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {externalConsumers.map((consumer, index) => (
                            <TableRow key={index}>
                                <TableCell>{toTitleCase(consumer.name) || 'N/A'}</TableCell>
                                <TableCell>{consumer.onboardingDate || 'N/A'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            </Collapse>
        </InfoCard>
    );
};

// Service Capabilities Table with controlled column widths
const ServiceCapabilitiesTable = ({ capabilities }: { capabilities: Record<string,CapabilityEntry>}) => {
    const capabilitiesArray = Object.entries(capabilities);
    const [isExpanded, setIsExpanded] = useState(false);
    if (capabilitiesArray.length === 0) return null;
    
    return (
        <InfoCard>
            <Box display="flex" alignItems="left" justifyContent="flex-start">
                <IconButton
                        size="small"
                        onClick={() => setIsExpanded(!isExpanded)}
                        aria-label="Toggle Service Capabilities details"
                        aria-expanded={isExpanded}
                        style={{
                            transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                            transition: 'transform 0.3s',
                            padding: 0,
                            backgroundColor: 'transparent',
                        }}
                        disableRipple
                    >
                        <ExpandMoreIcon />
                    </IconButton>
                <Box style={{ flex: 1 }}>
                    <div style={{ padding: '10px',fontSize:'20px',fontWeight:'bold' }}>Service Capabilities</div>
                </Box>
            </Box>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <div style={{ width: '100%' }}>
                <Table>
                    <TableBody>
                        {capabilitiesArray.map(([key, capability], index) => (
                            <TableRow key={index}>
                                <TableCell 
                                    component="th" 
                                    scope="row" 
                                    style={{ width: '30%', fontWeight: 600, cursor: 'pointer' }}
                                >
                                    <Tooltip title={capability.description} placement="bottom-start">
                                        <span>{key}</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell style={{ width: '70%', whiteSpace: 'pre-line', lineHeight: '1.5' }}>{formatCapabilityValue(key, capability.value)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            </Collapse>
        </InfoCard>
    );
};

// Databases Table: Render each list of tables as a bullet list and set "Name" and "isInSnowFlake" columns wider.
const DatabasesTable = ({ databases, streamsConsumed, streamsProduced }: { databases: any[], streamsConsumed: any, streamsProduced: any }) => {

    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <InfoCard>
            <Box display="flex" alignItems="left" justifyContent="flex-start">
                <IconButton
                        size="small"
                        onClick={() => setIsExpanded(!isExpanded)}
                        aria-label="Toggle Databases/Streams details"
                        aria-expanded={isExpanded}
                        style={{
                            transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                            transition: 'transform 0.3s',
                            padding: 0,
                            backgroundColor: 'transparent',
                        }}
                        disableRipple
                    >
                        <ExpandMoreIcon />
                </IconButton>
                <Box style={{ flex: 1}}>
                    <div style={{ padding: '10px',fontSize:'20px',fontWeight:'bold' }}>Data Sources</div>
                </Box>
            </Box>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <div style={{ width: '100%', overflowX: 'auto'}}>
                {/* Databases Section */}
                {databases && databases.length > 0 && (
                    <>
                        <div style={{ padding: '10px', fontSize: '16px', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                            {databases.length === 1 ? 'Database' : 'Databases'}
                        </div>
                        {(() => {
                            const hasSnowflake = databases.some((db: any) => db.isInSnowFlake === 'true');
                            
                            return (
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ whiteSpace: 'nowrap' }}>Name</TableCell>
                                            <TableCell style={{ whiteSpace: 'nowrap' }}>ARN</TableCell>
                                            <TableCell style={{ whiteSpace: 'nowrap' }}>isInSnowFlake</TableCell>
                                            {hasSnowflake && (
                                                <TableCell style={{ whiteSpace: 'nowrap' }}>Snowflake View Name</TableCell>
                                            )}
                                            <TableCell style={{ whiteSpace: 'nowrap' }}>List of Tables</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {databases.map((db, index) => (
                                            <TableRow key={index}>
                                                <TableCell style={{ whiteSpace: 'nowrap' }}>{db.name || 'N/A'}</TableCell>
                                                <TableCell style={{ whiteSpace: 'nowrap' }}>{db.arn || 'N/A'}</TableCell>
                                                <TableCell style={{ whiteSpace: 'nowrap' }}>{db.isInSnowFlake ?? 'N/A'}</TableCell>
                                                {hasSnowflake && (
                                                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                                                        {db.isInSnowFlake === 'true' ? (
                                                            db.snowflakeViewName ? (
                                                                <ul style={{ margin: 0, paddingLeft: '1em' }}>
                                                                    {db.snowflakeViewName.split(',').map((view: string, i: number) => (
                                                                        <li key={i} style={{ whiteSpace: 'nowrap' }}>{view.trim()}</li>
                                                                    ))}
                                                                </ul>
                                                            ) : 'N/A'
                                                        ) : 'N/A'}
                                                    </TableCell>
                                                )}
                                                <TableCell style={{ whiteSpace: 'nowrap' }}>
                                                    {Array.isArray(db.listOfTables) && db.listOfTables.length > 0 ? (
                                                        <ul style={{ margin: 0, paddingLeft: '1em' }}>
                                                            {db.listOfTables.map((table: string, i: number) => (
                                                                <li key={i} style={{ whiteSpace: 'nowrap' }}>{table}</li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        'N/A'
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            );
                        })()}
                    </>
                )}

                {/* Streams Produced Section */}
                {streamsProduced && streamsProduced.length > 0 && (
                    <>
                        <div style={{ padding: '10px', fontSize: '16px', fontWeight: 'bold', backgroundColor: '#f5f5f5', marginTop: databases && databases.length > 0 ? '20px' : '0' }}>
                            Streams Produced by Service
                        </div>
                        <div style={{ padding: '10px 20px' }}>
                            <ul style={{ margin: 0, paddingLeft: '1.5em', listStyleType: 'disc' }}>
                                {streamsProduced.map((stream: any, i: number) => (
                                    <li key={i} style={{ padding: '4px 0' }}>{stream.name || 'N/A'}</li>
                                ))}
                            </ul>
                        </div>
                    </>
                )}

                {/* Streams Consumed Section */}
                {streamsConsumed && streamsConsumed.length > 0 && (
                    <>
                        <div style={{ padding: '10px', fontSize: '16px', fontWeight: 'bold', backgroundColor: '#f5f5f5', marginTop: (databases && databases.length > 0) || (streamsProduced && streamsProduced.length > 0) ? '20px' : '0' }}>
                            Streams Consumed by Service
                        </div>
                        <div style={{ padding: '10px 20px' }}>
                            <ul style={{ margin: 0, paddingLeft: '1.5em', listStyleType: 'disc' }}>
                                {streamsConsumed.map((stream: any, i: number) => (
                                    <li key={i} style={{ padding: '4px 0' }}>{stream.name || 'N/A'}</li>
                                ))}
                            </ul>
                        </div>
                    </>
                )}
            </div>
            </Collapse>
        </InfoCard>
    );
};

const EntityFullApiDetailsCard = () => {
    const { entity } = useEntity<Entity>();
    console.log('Entity in Full API Details Card:', entity);
    const spec = (entity.spec ?? {}) as ApiEntitySpec;
    const contractTesting = spec.contractTesting ?? {};

    // --- Annotations: Build an object and sort keys alphabetically ---
    const annotations = {
        "API Ownership": entity.metadata.annotations?.['backstage.io/apiOwnership'],
        "Apigee Proxy": entity.metadata.annotations?.['backstage.io/apigeeProxy'],
        "Cron": entity.metadata.annotations?.['backstage.io/cron'],
        "DMP Domain": entity.metadata.annotations?.['backstage.io/dmpDomain'],
        "Managed-by Location": entity.metadata.annotations?.['backstage.io/managed-by-location'],
        "PII": entity.metadata.annotations?.['backstage.io/PII'],
        "Product Owner": entity.metadata.annotations?.['backstage.io/productOwner'],
        "Service Category": entity.metadata.annotations?.['backstage.io/serviceCategory'],
        "ServiceNow Assignment Group": entity.metadata.annotations?.['backstage.io/serviceNowAssignmentGroup'],
        "Service Owner": entity.metadata.annotations?.['backstage.io/serviceOwner'],
        "Service Scope": entity.metadata.annotations?.['backstage.io/serviceScope'],
        "Service Creation Date": entity.metadata.annotations?.['backstage.io/serviceCreationDate'],
        "OpenAPI Specification URL": entity.metadata.annotations?.['backstage.io/openApiSpecificationUrl']?.trim() || 'https://github.com/cat-digital-platform/P-Apigee-APIResources/tree/master/APISpecifications',
        "Target Basepath": entity.metadata.annotations?.['backstage.io/targetBasepath'],
        "Tech Lead": entity.metadata.annotations?.['backstage.io/techLead'],
        "Team": entity.metadata.annotations?.['backstage.io/team'],
        "Functional Leader": entity.metadata.annotations?.['backstage.io/functionalLeader'],
        "Troux Service": entity.metadata.annotations?.['backstage.io/trouxService'],
        [entity.metadata.annotations?.['backstage.io/leanIx'] ? 'LeanIX' : 'TrouxID']: entity.metadata.annotations?.['backstage.io/leanIx'] || entity.metadata.annotations?.['backstage.io/trouxId'],
    };

    const capabilityAnnotations= {
        "Asset Centric": {
            value: entity.metadata.annotations?.['backstage.io/assetCentric'],
            description: 'Services that provide asset centric details like make, serial number, etc. (in Request or Response).'
        },
        "Customer Merge Functionality": {
            value: entity.metadata.annotations?.['backstage.io/customerMergeFunctionality'],
            description: 'Services that respect customer consolidation event.'
        },
        "Enforces Entitlements": {
            value: entity.metadata.annotations?.['backstage.io/enforcesEntitlements'],
            description: 'Is service enforcing entitlements?'
        },
        "Entitlement CAT Corp Enabled": {
            value: entity.metadata.annotations?.['backstage.io/entitlementsCatCorpSupport'],
            description: 'Is CAT Corp enabled for this service?'
        },
        "Internal Access Logging": {
            value: entity.metadata.annotations?.['backstage.io/internalAccessLogging'],
            description: 'Services that logs requestor details like client ID, request payload, etc in their internal logging like AWS CloudWatch.'
        },
        "Localization": {
            value: entity.metadata.annotations?.['backstage.io/localization'],
            description: 'Support for different languages based on the API request.'
        },
        "Service To Service Communication Support": {
            value: entity.metadata.annotations?.['backstage.io/serviceToServiceCommunicationSupport'],
            description: 'Services that support access through internal gateway URL (Not only through APIGEE endpoint).'
        },
        "Forced Filter Support": {
            value: entity.metadata.annotations?.['backstage.io/forcedFilterSupport'],
            description: 'Services that force filters based on user details in OAuth token and entitlements header details that are provided in the API request.'
        },
        "CAT Support User Action Audit Logging":{
            value: entity.metadata.annotations?.['backstage.io/catSupportUserActionAuditLogging'],
            description: 'Support for logging user-level actions for support users.'
        },
        "Unauthenticated Access": {
            value: entity.metadata.annotations?.['backstage.io/unauthenticatedAccess'] || "false",
            description: 'Unauthenticated access is Public APIs that don’t require login, but only deliver safe, non-confidential data.'
        },
        // Contract Testing display logic
        ...(contractTesting.isEnabled === 'yes'|| contractTesting.isEnabled==='Yes' || contractTesting.isEnabled === 'true' ? {
            "Contract Testing: Is Enabled": {value:contractTesting.isEnabled,description: 'Indicates if contract testing is enabled for the service.'},
            "Contract Testing: List Of Applications": {value:contractTesting.listOfApplications,description: 'List of applications for which contract testing is enabled.'},
            "Contract Testing: Why Enabled": {value:contractTesting.reason,description: 'Reason why contract testing is enabled.'},
        } : contractTesting.isEnabled === 'no' || contractTesting.isEnabled === 'No' || contractTesting.isEnabled === 'false' ? {
            "Contract Testing: Is Enabled": {value:contractTesting.isEnabled,description: 'Indicates if contract testing is enabled for the service.'},
            "Contract Testing: List Of Applications": {value:contractTesting.listOfApplications,description: 'List of applications for which contract testing is disabled.'},
            "Contract Testing: Why Disabled": {value:contractTesting.reason,description: 'Reason why contract testing is disabled.'},
        } : contractTesting.isEnabled === 'not applicable' || contractTesting.isEnabled === 'Not Applicable' ? {
            "Contract Testing: Is Enabled": {value:contractTesting.isEnabled,description: 'Indicates if contract testing is enabled for the service.'},
            "Contract Testing: List Of Applications": {value:contractTesting.listOfApplications,description: 'List of applications for which contract testing is not applicable.'},
            "Contract Testing: Why Not Applicable": {value:contractTesting.reason,description: 'Reason why contract testing is not applicable.'},
        } : {}),
    };

    const sortedCapabilityAnnotations: Record<string, CapabilityEntry> = {};
    Object.entries(capabilityAnnotations)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([key, capability]) => {
            const rawValue = capability.value;
            const formattedValue =
                typeof rawValue === 'string' && key.startsWith('Contract Testing: Why')
                    ? formatReasonWithLineBreaks(rawValue.trim())
                    : formatMetadataValue(rawValue);
            if (formattedValue !== '' && formattedValue != null) {
                sortedCapabilityAnnotations[key] = {
                    ...capability,
                    value: formattedValue,
                };
            }
        });


    const sortedAnnotations: Record<string, any> = {};
    Object.entries(annotations)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([key, value]) => {
            const formatted = formatMetadataValue(value, key);
            if (formatted !== '' && formatted != null) {
                sortedAnnotations[key] = formatted;
            }
        });

    // --- Spec Details: We remove definition from here ---
    const specProperties = {};
    const filteredSpecProps = filterProps(specProperties);

    // --- External Consumers and Databases ---
    const externalConsumers = entity.spec?.externalConsumers;
    const databases = entity.spec?.databases;
    const consumedStreams = entity.spec?.streamsConsumedByService;
    const producedStreams = entity.spec?.streamsProducedByService;
    const apiDetailsCount = Object.keys(sortedAnnotations).length;
    const capabilityAnnotationsCount = Object.keys(sortedCapabilityAnnotations).length;
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <>
        <InfoCard>
            <Box display="flex" alignItems="left" justifyContent="flex-start">
                <IconButton
                        size="small"
                        onClick={() => setIsExpanded(!isExpanded)}
                        aria-label="Toggle Service Details"
                        aria-expanded={isExpanded}
                        style={{
                            transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                            transition: 'transform 0.3s',
                            padding: 0,
                            backgroundColor: 'transparent',
                        }}
                        disableRipple
                    >
                        <ExpandMoreIcon />
                </IconButton>
                <Box style={{ flex: 1 }}>
                    <div style={{ padding: '10px',fontSize:'20px',fontWeight:'bold' }}>Service Details</div>

                </Box>
            </Box>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Grid container spacing={3}>
                {/* Annotations Section - Full Width */}
                {apiDetailsCount > 0 && (
                    <Grid item xs={12}>
                        <div style={{ width: '100%' }}>
                            <StructuredMetadataTable metadata={sortedAnnotations} />
                        </div>
                    </Grid>
                )}
                {/* Spec Details Section (if any) - Full Width */}
                {Object.keys(filteredSpecProps).length > 0 && (
                    <Grid item xs={12}>
                        <div style={{ width: '100%' }}>
                            <StructuredMetadataTable metadata={filteredSpecProps} />
                        </div>
                    </Grid>
                )}
            </Grid>
            </Collapse>
        </InfoCard>
        {capabilityAnnotationsCount > 0 && (
            <Grid item xs={12}>
                <ServiceCapabilitiesTable capabilities={sortedCapabilityAnnotations} />
            </Grid>
        )}
        {/* Databases Section */}
        {((databases && databases.length > 0) || 
          (consumedStreams && consumedStreams.length > 0) || 
          (producedStreams && producedStreams.length > 0)) && (
            <Grid item xs={12}>
                <DatabasesTable databases={databases} streamsConsumed ={consumedStreams} streamsProduced={producedStreams} />
            </Grid>
        )}
        {/* External Consumers Section */}
        {externalConsumers && externalConsumers.length > 0 && (
            <Grid item xs={12}>
                <ExternalConsumersTable externalConsumers={externalConsumers} />
            </Grid>
        )}     
    </>
    );
};

export default EntityFullApiDetailsCard;
